import type { PlaybackAdapter, TimelineEntry, TimelineUpdate } from "@stimulantnoise/protocol";

export interface PlaybackInit {
  sessionId: string;
  preferredAdapter?: "auto" | "mse" | "hls" | "native";
  targetReserveMs: number;
}

export interface PlaybackCapabilities {
  adapter: PlaybackAdapter;
  codecs: string[];
  backgroundPlayback: boolean;
  localDsp: boolean;
  canRemoveStagedItems: boolean;
}

export interface LocalResponse {
  energyDelta: number;
  rampMs: number;
  expiresAfterMs: number;
}

export interface PlaybackState {
  status: "idle" | "buffering" | "playing" | "paused" | "stalled" | "ended" | "error";
  positionMs: number;
  committedBufferedMs: number;
  totalBufferedMs: number;
  timelineRevision: number;
}

export type PlaybackEvent =
  | { type: "state"; state: PlaybackState }
  | { type: "stall"; startedAt: number }
  | { type: "stall-ended"; startedAt: number; durationMs: number }
  | { type: "gap"; durationMs: number }
  | { type: "append-complete"; sequence: number; timelineRevision: number }
  | { type: "item-start"; entryId: string; sequence: number }
  | { type: "error"; code: string; recoverable: boolean };

export interface PlaybackFacade {
  initialize(options: PlaybackInit): Promise<PlaybackCapabilities>;
  applyTimeline(update: TimelineUpdate): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  seekCommitted(positionMs: number): Promise<void>;
  setLocalResponse(value: LocalResponse): Promise<void>;
  getState(): Promise<PlaybackState>;
  subscribe(listener: (event: PlaybackEvent) => void): () => void;
  destroy(): Promise<void>;
}

const MIME = 'audio/mp4; codecs="mp4a.40.2"';

export function mediaPlacement(entry: TimelineEntry): { timestampOffsetSeconds: number; appendWindowStartSeconds: number; appendWindowEndSeconds: number } {
  const fallbackPart = Number(entry.url.match(/\/segment-(\d+)\.m4s/)?.[1] ?? 0);
  const mediaTimeMs = entry.media_time_ms ?? fallbackPart * 4_000;
  if (entry.start_sample !== undefined && entry.media_time_samples !== undefined) {
    const startSeconds = entry.start_sample / 48_000;
    const durationSeconds = (entry.media_duration_samples ?? Math.round(entry.duration_ms * 48)) / 48_000;
    return {
      timestampOffsetSeconds: (entry.start_sample - entry.media_time_samples) / 48_000,
      appendWindowStartSeconds: Math.max(0, startSeconds - 1 / 48_000),
      appendWindowEndSeconds: startSeconds + durationSeconds + 1 / 48_000
    };
  }
  return {
    timestampOffsetSeconds: (entry.start_ms - mediaTimeMs) / 1_000,
    appendWindowStartSeconds: Math.max(0, entry.start_ms / 1_000 - 0.001),
    appendWindowEndSeconds: (entry.start_ms + entry.duration_ms) / 1_000 + 0.001
  };
}

export function committedSequencesToQueue(
  entries: TimelineEntry[],
  appended: ReadonlySet<number>,
  queued: ReadonlySet<number>
): TimelineEntry[] {
  return entries.filter((entry) =>
    entry.state === "committed"
    && !appended.has(entry.sequence)
    && !queued.has(entry.sequence)
  );
}

function bufferedAhead(element: HTMLAudioElement): number {
  const current = element.currentTime;
  for (let index = 0; index < element.buffered.length; index += 1) {
    if (element.buffered.start(index) <= current && element.buffered.end(index) >= current) {
      return Math.max(0, (element.buffered.end(index) - current) * 1000);
    }
  }
  return 0;
}

export class WebPlaybackFacade implements PlaybackFacade {
  readonly #listeners = new Set<(event: PlaybackEvent) => void>();
  readonly #element: HTMLAudioElement;
  #audioContext?: AudioContext;
  #gainNode?: GainNode;
  #mediaSource: MediaSource | undefined;
  #sourceBuffer: SourceBuffer | undefined;
  #objectUrl: string | undefined;
  #adapter: PlaybackAdapter = "simple-queue";
  #revision = -1;
  #entries: TimelineEntry[] = [];
  #appendedSequences = new Set<number>();
  #queuedSequences = new Set<number>();
  #appendChain = Promise.resolve();
  #initialized = false;
  #initRef?: string;
  #stallStartedAt: number | undefined;
  #lastReportedGap: string | undefined;
  #activeSequence: number | undefined;

  constructor(element?: HTMLAudioElement) {
    this.#element = element ?? document.createElement("audio");
    this.#element.preload = "auto";
    this.#element.addEventListener("waiting", () => {
      if (this.#stallStartedAt === undefined && this.#element.currentTime > 0) {
        this.#stallStartedAt = Date.now();
        this.#emit({ type: "stall", startedAt: this.#stallStartedAt });
      }
    });
    this.#element.addEventListener("playing", () => this.#finishStall());
    this.#element.addEventListener("error", () => this.#emit({ type: "error", code: "MEDIA_ELEMENT", recoverable: true }));
    for (const eventName of ["play", "pause", "waiting", "ended", "timeupdate"] as const) {
      this.#element.addEventListener(eventName, () => void this.#emitState());
    }
  }

  get element(): HTMLAudioElement {
    return this.#element;
  }

  async initialize(options: PlaybackInit): Promise<PlaybackCapabilities> {
    if (this.#initialized) return this.#capabilities();
    const canMse = typeof MediaSource !== "undefined" && MediaSource.isTypeSupported(MIME);
    const canNativeHls = this.#element.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (options.preferredAdapter === "hls" || (!canMse && canNativeHls)) {
      this.#adapter = "hls";
    } else if (canMse) {
      this.#adapter = "mse";
      try {
        await this.#initializeMse();
      } catch {
        this.#teardownMse();
        this.#adapter = canNativeHls ? "hls" : "simple-queue";
      }
    } else {
      this.#adapter = "simple-queue";
    }
    this.#initialized = true;
    return this.#capabilities();
  }

  async #initializeMse(): Promise<void> {
    this.#mediaSource = new MediaSource();
    this.#objectUrl = URL.createObjectURL(this.#mediaSource);
    this.#element.src = this.#objectUrl;
    await new Promise<void>((resolve, reject) => {
      const source = this.#mediaSource!;
      const onOpen = () => { cleanup(); resolve(); };
      const onClose = () => { cleanup(); reject(new Error("media source closed during setup")); };
      const timer = window.setTimeout(() => { cleanup(); reject(new Error("media source open timed out")); }, 3_000);
      const cleanup = () => {
        window.clearTimeout(timer);
        source.removeEventListener("sourceopen", onOpen);
        source.removeEventListener("sourceclose", onClose);
      };
      source.addEventListener("sourceopen", onOpen, { once: true });
      source.addEventListener("sourceclose", onClose, { once: true });
    });
    this.#sourceBuffer = this.#mediaSource.addSourceBuffer(MIME);
    this.#sourceBuffer.mode = "segments";
  }

  #teardownMse(): void {
    this.#sourceBuffer = undefined;
    this.#mediaSource = undefined;
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
    this.#objectUrl = undefined;
    this.#element.removeAttribute("src");
    this.#element.load();
  }

  #capabilities(): PlaybackCapabilities {
    return {
      adapter: this.#adapter,
      codecs: ["mp4a.40.2"],
      backgroundPlayback: false,
      localDsp: typeof AudioContext !== "undefined",
      canRemoveStagedItems: this.#adapter === "mse"
    };
  }

  async applyTimeline(update: TimelineUpdate): Promise<void> {
    if (update.timeline_revision <= this.#revision) return;
    if (update.replace_from_sequence !== undefined) {
      const conflict = this.#entries.find((entry) => entry.sequence >= update.replace_from_sequence! && entry.state === "committed");
      if (conflict) throw new Error(`cannot replace committed sequence ${conflict.sequence}`);
      this.#entries = this.#entries.filter((entry) => entry.sequence < update.replace_from_sequence!);
    }
    const merged = new Map(this.#entries.map((entry) => [entry.sequence, entry]));
    for (const entry of update.entries) merged.set(entry.sequence, entry);
    this.#entries = [...merged.values()].sort((a, b) => a.sequence - b.sequence);
    this.#revision = update.timeline_revision;

    if (this.#adapter === "mse") {
      const committed = committedSequencesToQueue(this.#entries, this.#appendedSequences, this.#queuedSequences);
      for (const entry of committed) this.#queueAppend(entry);
      await this.#appendChain;
    } else {
      throw new Error("generated startup catalog requires an MSE-capable playback adapter");
    }
    await this.#emitState();
  }

  #queueAppend(entry: TimelineEntry): void {
    this.#queuedSequences.add(entry.sequence);
    this.#appendChain = this.#appendChain.then(async () => {
      if (!this.#sourceBuffer) return;
      const placement = mediaPlacement(entry);
      this.#sourceBuffer.timestampOffset = placement.timestampOffsetSeconds;
      this.#sourceBuffer.appendWindowStart = placement.appendWindowStartSeconds;
      this.#sourceBuffer.appendWindowEnd = placement.appendWindowEndSeconds;
      if (entry.init_ref && this.#initRef !== entry.init_ref) {
        await this.#appendBytes(await this.#fetchBytes(entry.init_ref));
        this.#initRef = entry.init_ref;
      }
      await this.#appendBytes(await this.#fetchBytes(entry.url));
      this.#appendedSequences.add(entry.sequence);
      this.#emit({ type: "append-complete", sequence: entry.sequence, timelineRevision: this.#revision });
      await this.#evictOldBuffer();
    }).catch(() => {
      this.#emit({ type: "error", code: "MSE_APPEND", recoverable: true });
    }).finally(() => {
      this.#queuedSequences.delete(entry.sequence);
    });
  }

  async #fetchBytes(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url, { cache: "force-cache" });
    if (!response.ok) throw new Error(`media fetch failed: ${response.status}`);
    return response.arrayBuffer();
  }

  async #appendBytes(bytes: ArrayBuffer): Promise<void> {
    const source = this.#sourceBuffer;
    if (!source) return;
    await new Promise<void>((resolve, reject) => {
      const onEnd = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); reject(new Error("source buffer append failed")); };
      const cleanup = () => {
        source.removeEventListener("updateend", onEnd);
        source.removeEventListener("error", onError);
      };
      source.addEventListener("updateend", onEnd, { once: true });
      source.addEventListener("error", onError, { once: true });
      source.appendBuffer(bytes);
    });
  }

  async #evictOldBuffer(): Promise<void> {
    const source = this.#sourceBuffer;
    if (!source || source.updating || this.#element.currentTime < 45 || source.buffered.length === 0) return;
    const removeUntil = Math.max(0, this.#element.currentTime - 30);
    if (removeUntil <= 0) return;
    await new Promise<void>((resolve, reject) => {
      const onEnd = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); reject(new Error("source buffer eviction failed")); };
      const cleanup = () => {
        source.removeEventListener("updateend", onEnd);
        source.removeEventListener("error", onError);
      };
      source.addEventListener("updateend", onEnd, { once: true });
      source.addEventListener("error", onError, { once: true });
      source.remove(0, removeUntil);
    });
  }

  async play(): Promise<void> {
    if (!this.#initialized) throw new Error("playback is not initialized");
    // Start both activation-sensitive operations in the original click task.
    // Awaiting either one first causes WebKit to discard transient user activation.
    const graph = this.#ensureAudioGraph();
    const media = this.#element.play();
    await Promise.all([graph, media]);
  }

  async pause(): Promise<void> {
    this.#element.pause();
  }

  async seekCommitted(positionMs: number): Promise<void> {
    const committedUntil = Math.max(0, ...this.#entries.filter((entry) => entry.state === "committed").map((entry) => entry.start_ms + entry.duration_ms));
    if (positionMs < 0 || positionMs > committedUntil) throw new RangeError("seek must remain within committed media");
    this.#element.currentTime = positionMs / 1000;
  }

  async #ensureAudioGraph(): Promise<void> {
    if (typeof AudioContext === "undefined") return;
    if (!this.#audioContext) {
      this.#audioContext = new AudioContext();
      const source = this.#audioContext.createMediaElementSource(this.#element);
      this.#gainNode = this.#audioContext.createGain();
      source.connect(this.#gainNode).connect(this.#audioContext.destination);
    }
    if (this.#audioContext.state === "suspended") await this.#audioContext.resume();
  }

  async setLocalResponse(value: LocalResponse): Promise<void> {
    await this.#ensureAudioGraph();
    if (!this.#audioContext || !this.#gainNode) return;
    const now = this.#audioContext.currentTime;
    const target = Math.min(1.12, Math.max(0.88, 1 + value.energyDelta * 0.12));
    this.#gainNode.gain.cancelScheduledValues(now);
    this.#gainNode.gain.setValueAtTime(this.#gainNode.gain.value, now);
    this.#gainNode.gain.linearRampToValueAtTime(target, now + value.rampMs / 1000);
    this.#gainNode.gain.linearRampToValueAtTime(1, now + (value.rampMs + value.expiresAfterMs) / 1000);
  }

  async getState(): Promise<PlaybackState> {
    const status: PlaybackState["status"] = this.#element.error
      ? "error"
      : this.#element.ended
        ? "ended"
        : this.#element.paused
          ? "paused"
          : this.#element.readyState < 3
            ? "buffering"
            : "playing";
    const buffered = bufferedAhead(this.#element);
    this.#reportBufferedHole();
    return {
      status,
      positionMs: Math.round(this.#element.currentTime * 1000),
      committedBufferedMs: Math.round(buffered),
      totalBufferedMs: Math.round(buffered),
      timelineRevision: this.#revision
    };
  }

  subscribe(listener: (event: PlaybackEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(event: PlaybackEvent): void {
    for (const listener of this.#listeners) listener(event);
  }

  async #emitState(): Promise<void> {
    const state = await this.getState();
    this.#emit({ type: "state", state });
    const currentEntry = this.#entries.find((entry) =>
      state.positionMs >= entry.start_ms && state.positionMs < entry.start_ms + entry.duration_ms
    );
    if (currentEntry && currentEntry.sequence !== this.#activeSequence) {
      this.#activeSequence = currentEntry.sequence;
      this.#emit({ type: "item-start", entryId: currentEntry.entry_id, sequence: currentEntry.sequence });
    }
  }

  #finishStall(): void {
    if (this.#stallStartedAt === undefined) return;
    const startedAt = this.#stallStartedAt;
    this.#stallStartedAt = undefined;
    this.#emit({ type: "stall-ended", startedAt, durationMs: Date.now() - startedAt });
  }

  #reportBufferedHole(): void {
    const ranges = this.#element.buffered;
    const current = this.#element.currentTime;
    for (let index = 0; index + 1 < ranges.length; index += 1) {
      const end = ranges.end(index);
      const nextStart = ranges.start(index + 1);
      if (current >= end - 0.02 && current <= nextStart + 0.02 && nextStart - end > 0.01) {
        const key = `${end.toFixed(3)}:${nextStart.toFixed(3)}`;
        if (key !== this.#lastReportedGap) {
          this.#lastReportedGap = key;
          this.#emit({ type: "gap", durationMs: Math.round((nextStart - end) * 1000) });
        }
        return;
      }
    }
  }

  async destroy(): Promise<void> {
    this.#element.pause();
    this.#teardownMse();
    await this.#audioContext?.close();
    this.#listeners.clear();
    this.#queuedSequences.clear();
    this.#appendedSequences.clear();
    this.#initialized = false;
  }
}
